import { useState } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';
import { ACCENT, OK, BAD, WARN, VIOLET } from '../../lib/uiTokens';
import useSirokyDisplej from '../../hooks/useSirokyDisplej';
import MesicniGraf from './MesicniGraf';

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v));

/**
 * Pravý panel statistik DPH — vejde se na výšku stránky, nikdy neroluje.
 *
 * Drží jen to, co hlavní tabulka vlevo neukazuje: kolik se tenhle měsíc odvádí
 * a jestli je to zaplacené, odkud se odvod bere (výstup vs. vstup) a roční
 * průběh. Sazbové bary a YTD karta odsud vypadly — rozpad per sazba i roční
 * součty jsou v tabulce „Rozpad DPH podle sazeb" přesněji a v číslech.
 *
 * Od `xl` výš je panel `h-full`: karty nahoře mají pevnou výšku a graf dobere
 * zbytek (`fill`). Pod `xl` se řadí pod tabulky a roste přirozeně, proto `fill`
 * jen na širokém displeji — v auto výšce by procentní sloupce grafu zkolabovaly.
 */
export default function DphStatistikyPanel({
    data, rocniData = [], rok, mesic, cardStyle, onZaplaceno, ukladani,
}) {
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datumZaplaceni, setDatumZaplaceni] = useState(new Date().toISOString().slice(0, 10));
    const naVysku = useSirokyDisplej(1280);

    if (!data) return null;

    const vystup = data.vystup || {};
    const vstup = data.vstup || {};
    const zaplaceno = data.stav_odvodu === 'zaplaceno';

    // Termín odvodu — do 25. následujícího měsíce
    const terminMesic = mesic === 12 ? 1 : mesic + 1;
    const terminRok = mesic === 12 ? rok + 1 : rok;

    const zbyvaUhradit = rocniData
        .filter(r => r.stav_odvodu !== 'zaplaceno')
        .reduce((s, r) => s + (r.dph_k_odvodu || 0), 0);

    const Karta = ({ children, className = '', style }) => (
        <div className={`rounded-2xl p-4 flex-shrink-0 ${className}`} style={{ ...cardStyle, ...style }}>{children}</div>
    );

    return (
        <div className={`flex flex-col gap-3 ${naVysku ? 'h-full' : ''}`}>
            {/* Hlavní číslo — DPH k odvodu za vybraný měsíc */}
            <Karta style={{ background: `${WARN}14`, border: `1px solid ${WARN}44` }}>
                <div className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: WARN }}>
                    DPH k odvodu — {MESICE[mesic - 1]} {rok}
                </div>
                <div className="text-3xl font-bold tabular-nums" style={{ color: WARN }}>{fmt(data.dph_k_odvodu)} Kč</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    Termín: do 25. {terminMesic}. {terminRok}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {zaplaceno ? (
                        <Badge barva={OK} icon={Check}>
                            Zaplaceno {data.datum_zaplaceni ? new Date(data.datum_zaplaceni).toLocaleDateString('cs-CZ') : ''}
                        </Badge>
                    ) : (
                        <>
                            <Badge barva={BAD} icon={AlertCircle}>Nezaplaceno</Badge>
                            {!showDatePicker ? (
                                <button onClick={() => setShowDatePicker(true)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                                    style={{ background: OK }}>
                                    Označit jako zaplaceno
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <input type="date" value={datumZaplaceni}
                                        onChange={e => setDatumZaplaceni(e.target.value)}
                                        className="rounded-lg px-2 py-1.5 text-xs"
                                        style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                                    <button onClick={() => onZaplaceno(datumZaplaceni).then(() => setShowDatePicker(false))}
                                        disabled={ukladani}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white"
                                        style={{ background: OK, opacity: ukladani ? 0.5 : 1 }}>
                                        <Check size={14} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Karta>

            {/* Odkud se odvod bere — výstup vs. vstup, se sazbovým podpisem */}
            <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                <Karta>
                    <div className="text-xs uppercase" style={{ color: 'var(--text-muted)' }}>DPH výstup</div>
                    <div className="text-2xl font-bold tabular-nums" style={{ color: ACCENT }}>{fmt(vystup.dph_celkem)}</div>
                    <SazbovyPodpis d12={vystup.dph_12} d21={vystup.dph_21} />
                </Karta>
                <Karta>
                    <div className="text-xs uppercase" style={{ color: 'var(--text-muted)' }}>DPH vstup</div>
                    <div className="text-2xl font-bold tabular-nums" style={{ color: OK }}>{fmt(vstup.dph_celkem)}</div>
                    <SazbovyPodpis d12={vstup.dph_12} d21={vstup.dph_21} />
                </Karta>
            </div>

            {/* Plnění bez vyčíslené daně — reverse charge / EU / dovoz */}
            {vstup.samovymereni_pocet > 0 && (
                <Karta style={{ background: `${WARN}0f`, border: `1px solid ${WARN}33` }}>
                    <div className="flex items-start gap-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        <Info size={13} style={{ color: WARN, flexShrink: 0, marginTop: 1 }} />
                        <span>
                            <span className="font-semibold" style={{ color: WARN }}>
                                {vstup.samovymereni_pocet}× samovyměření
                            </span>{' '}
                            za {fmt(vstup.samovymereni_castka)} Kč — daň na dokladu není, v rozpadu sazeb chybí.
                        </span>
                    </div>
                </Karta>
            )}

            {/* Roční průběh — graf dobere zbytek výšky panelu */}
            {rocniData.length > 0 && (
                <div className={`rounded-2xl p-4 ${naVysku ? 'flex-1 min-h-0 flex flex-col' : ''}`} style={cardStyle}>
                    <div className="flex items-center justify-between gap-2 mb-3 flex-shrink-0">
                        <span className="text-xs uppercase" style={{ color: 'var(--text-muted)' }}>DPH měsíčně {rok}</span>
                        <span className="text-[11px] tabular-nums whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                            Zbývá uhradit{' '}
                            <span className="font-bold" style={{ color: zbyvaUhradit > 0 ? BAD : OK }}>{fmt(zbyvaUhradit)} Kč</span>
                        </span>
                    </div>
                    <div className={naVysku ? 'flex-1 min-h-0' : ''}>
                        <MesicniGraf
                            rok={rok}
                            fill={naVysku}
                            vyska={150}
                            data={rocniData.map(m => ({
                                mesic: `${rok}-${String(m.mesic).padStart(2, '0')}`,
                                vystup: m.dph_vystup_celkem || 0,
                                vstup: m.dph_vstup_celkem || 0,
                            }))}
                            series={[
                                { key: 'vystup', label: 'Výstup', color: ACCENT },
                                { key: 'vstup', label: 'Vstup', color: OK },
                            ]}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

const SazbovyPodpis = ({ d12, d21 }) => (
    <div className="flex items-center gap-2.5 mt-1.5 text-[10.5px] tabular-nums">
        <span className="flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
            <span style={{ width: 6, height: 6, borderRadius: 2, background: OK, display: 'inline-block' }} />
            12 % {fmt(d12)}
        </span>
        <span className="flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
            <span style={{ width: 6, height: 6, borderRadius: 2, background: VIOLET, display: 'inline-block' }} />
            21 % {fmt(d21)}
        </span>
    </div>
);

function Badge({ barva, icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
            style={{ background: `${barva}1f`, color: barva, border: `1px solid ${barva}44` }}>
            <Icon size={12} />{children}
        </span>
    );
}
